import React, { useState, useCallback } from 'react';
import moment from 'moment';
import type { Granularity, DateRangePreset, TimeframeSelection } from '../../types/timeframe';
import { DEFAULT_PRESET, DEFAULT_QUARTERLY_PRESET } from '../../types/timeframe';
import { computeDateRange } from '../../pages/usageReportingContainer/utils/computeDateRange';
import AggregateBySelector from './AggregateBySelector';
import DateRangeSelector from './DateRangeSelector';
import DayCalendar from './DayCalendar';
import styles from './advancedDatepickerPanel.module.css';

const MAX_DAILY_RANGE_DAYS = 60; // Inclusive window: 61 days max.

interface AdvancedDatepickerPanelProps {
  initialValue: TimeframeSelection;
  onApply: (selection: TimeframeSelection) => void;
  onCancel: () => void;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
}

const AdvancedDatepickerPanel: React.FC<AdvancedDatepickerPanelProps> = ({
  initialValue,
  onApply,
  onCancel,
  minDate,
  maxDate,
}) => {
  const [granularity, setGranularity] = useState<Granularity>(initialValue.granularity);
  const [preset, setPreset] = useState<DateRangePreset>(initialValue.preset);
  const [startDate, setStartDate] = useState<string>(initialValue.startDate);
  const [endDate, setEndDate] = useState<string>(initialValue.endDate);
  const [dailyCustomStartSelected, setDailyCustomStartSelected] = useState(false);

  const isDailyCustomRestrictionActive =
    granularity === 'daily' && preset === 'custom' && dailyCustomStartSelected;

  const clampDailyStart = useCallback((start: string, end: string): string => {
    if (!isDailyCustomRestrictionActive) return start;
    const minStart = moment(end, 'YYYY-MM-DD').subtract(MAX_DAILY_RANGE_DAYS, 'days').format('YYYY-MM-DD');
    return start < minStart ? minStart : start;
  }, [isDailyCustomRestrictionActive]);

  const clampDailyEnd = useCallback((start: string, end: string): string => {
    if (!isDailyCustomRestrictionActive) return end;
    const maxEnd = moment(start, 'YYYY-MM-DD').add(MAX_DAILY_RANGE_DAYS, 'days').format('YYYY-MM-DD');
    return end > maxEnd ? maxEnd : end;
  }, [isDailyCustomRestrictionActive]);

  const handleGranularityChange = useCallback((g: Granularity) => {
    setGranularity(g);
    setDailyCustomStartSelected(false);
    // When switching granularity, reset to appropriate default preset
    const defaultPreset = g === 'quarterly' ? DEFAULT_QUARTERLY_PRESET : DEFAULT_PRESET;
    const range = computeDateRange(defaultPreset);
    setPreset(defaultPreset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  }, []);

  const handlePresetChange = useCallback(
    (p: DateRangePreset, start: string, end: string) => {
      setPreset(p);
      setStartDate(start);
      setEndDate(end);
      setDailyCustomStartSelected(false);
    },
    []
  );

  const handleStartDateChange = useCallback(
    (dateStr: string) => {
      const customRestriction = granularity === 'daily';
      let nextStart = dateStr;
      let nextEnd = endDate;

      // If start is after end, move end to match.
      if (nextStart > nextEnd) {
        nextEnd = nextStart;
      }

      if (customRestriction) {
        const maxEnd = moment(nextStart, 'YYYY-MM-DD').add(MAX_DAILY_RANGE_DAYS, 'days').format('YYYY-MM-DD');
        if (nextEnd > maxEnd) {
          nextEnd = maxEnd;
        }
      }

      setStartDate(nextStart);
      setEndDate(nextEnd);
      setPreset('custom');
      if (customRestriction) {
        setDailyCustomStartSelected(true);
      }
    },
    [endDate, granularity]
  );

  const handleEndDateChange = useCallback(
    (dateStr: string) => {
      let nextStart = startDate;
      let nextEnd = dateStr;

      // If end is before start, move start to match.
      if (nextEnd < nextStart) {
        nextStart = nextEnd;
      }

      nextStart = clampDailyStart(nextStart, nextEnd);
      nextEnd = clampDailyEnd(nextStart, nextEnd);

      setStartDate(nextStart);
      setEndDate(nextEnd);
      setPreset('custom');
    },
    [startDate, clampDailyStart, clampDailyEnd]
  );

  const handleApply = () => {
    const safeStart = clampDailyStart(startDate, endDate);
    const safeEnd = clampDailyEnd(safeStart, endDate);
    onApply({ granularity, preset, startDate: safeStart, endDate: safeEnd });
  };

  const handleCancel = () => {
    setGranularity(initialValue.granularity);
    setPreset(initialValue.preset);
    setStartDate(initialValue.startDate);
    setEndDate(initialValue.endDate);
    onCancel();
  };

  const calendarMode = granularity === 'quarterly' ? 'quarter' : 'day';
  const disableCalendarDropdowns = granularity === 'daily' && preset !== 'custom';

  const startMinDate =
    isDailyCustomRestrictionActive
      ? (minDate
          ? moment.max(minDate.clone(), moment(endDate, 'YYYY-MM-DD').subtract(MAX_DAILY_RANGE_DAYS, 'days'))
          : moment(endDate, 'YYYY-MM-DD').subtract(MAX_DAILY_RANGE_DAYS, 'days'))
      : minDate;

  const startMaxDate =
    isDailyCustomRestrictionActive
      ? (maxDate
          ? moment.min(maxDate.clone(), moment(endDate, 'YYYY-MM-DD'))
          : moment(endDate, 'YYYY-MM-DD'))
      : maxDate;

  const endMinDate =
    granularity === 'daily' && preset === 'custom'
      ? (minDate
          ? moment.max(minDate.clone(), moment(startDate, 'YYYY-MM-DD'))
          : moment(startDate, 'YYYY-MM-DD'))
      : minDate;

  const endMaxDate =
    isDailyCustomRestrictionActive
      ? (maxDate
          ? moment.min(maxDate.clone(), moment(startDate, 'YYYY-MM-DD').add(MAX_DAILY_RANGE_DAYS, 'days'))
          : moment(startDate, 'YYYY-MM-DD').add(MAX_DAILY_RANGE_DAYS, 'days'))
      : maxDate;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {/* Column 1: Aggregate By */}
        <div className={styles.aggregateCol}>
          <AggregateBySelector value={granularity} onChange={handleGranularityChange} />
        </div>

        {/* Column 2: Date Range Presets */}
        <div className={styles.presetCol}>
          <DateRangeSelector value={preset} onChange={handlePresetChange} granularity={granularity} />
        </div>

        {/* Column 3: Start Date Calendar */}
        <div className={styles.calendarCol}>
          <DayCalendar
            selectedDate={startDate}
            onChange={handleStartDateChange}
            rangeOtherEnd={endDate}
            role="start"
            minDate={startMinDate}
            maxDate={startMaxDate}
            mode={calendarMode}
            disableDropdowns={disableCalendarDropdowns}
          />
        </div>

        {/* Dash connector between calendars */}
        <div className={styles.calendarDash} aria-hidden="true" />

        {/* Column 4: End Date Calendar */}
        <div className={styles.calendarCol}>
          <DayCalendar
            selectedDate={endDate}
            onChange={handleEndDateChange}
            rangeOtherEnd={startDate}
            role="end"
            minDate={endMinDate}
            maxDate={endMaxDate}
            mode={calendarMode}
            disableDropdowns={disableCalendarDropdowns}
          />
        </div>
      </div>

      {/* Footer: Cancel / Apply */}
      <div className={styles.footer}>
        <button className={styles.cancelBtn} type="button" onClick={handleCancel}>
          Cancel
        </button>
        <button className={styles.applyBtn} type="button" onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default AdvancedDatepickerPanel;
